/**
 * AssetProfileEnrichmentBanner — V22.9.8
 *
 * Non-blocking yellow banner shown on the dashboard for users who already
 * submitted the V22.9.5 Profile Gate but haven't yet added the V22.9.8
 * Asset Demography fields (vlan_id, firewall_zones, connectivity_rows, etc.).
 *
 * Detection: looks at the most recent asset_profiles row owned by the user
 * (via GET /asset-profiles/picker?owner_email=...). If `details` lacks any
 * of the V22.9.8 indicator fields, the banner renders. One-click navigates
 * to /profile-update?enrich=1.
 *
 * Dismissible (per-user localStorage key) — never blocks workflow.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Sparkles, X } from "lucide-react";

const DISMISS_KEY = "v22_9_8_enrichment_banner_dismissed";

// Roles that own an asset_profile (admins / BCM Mgrs / Dept Heads are exempt)
const ELIGIBLE_ROLES = new Set([
  "process_manager", "process_contributor",
  "app_manager", "application_manager", "app_contributor",
  "it_function_manager", "it_drp_manager", "it_function_contributor",
  "facility_manager", "facility_owner",
  "vendor_manager", "vendor_contact",
  "asset_owner",
]);

// V22.9.8 indicator fields — presence of any one signals the user has
// already enriched their profile.
const V22_9_8_INDICATORS = [
  "vlan_id", "load_balancer_type", "firewall_zones", "dns_names",
  "connectivity_rows", "regulatory_scope", "site_tier", "power_kva",
  "app_id_code", "tech_stack",
];

export default function AssetProfileEnrichmentBanner() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!user?.role || !ELIGIBLE_ROLES.has(user.role)) return;
      if (!user?.profile_completed) return; // they'll be force-routed to /profile-update anyway
      if (localStorage.getItem(DISMISS_KEY) === user.email) return;
      try {
        // Tiny credit-efficient call — picker is already used elsewhere
        const res = await api.get("/asset-profiles/picker", { params: { q: "" } });
        const mine = (res.data?.results || []).find((r) => r.owner_email === user.email);
        if (!mine?.id) return; // edge case — no profile to enrich yet
        const detail = await api.get(`/asset-profiles/by-id/${mine.id}`);
        const details = detail.data?.details || {};
        const enriched = V22_9_8_INDICATORS.some((k) => {
          const v = details[k];
          return v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
        });
        if (!cancelled && !enriched) setShow(true);
      } catch (e) {
        /* silent — banner is non-critical */
      }
    }
    check();
    return () => { cancelled = true; };
  }, [user, api]);

  if (!show) return null;
  return (
    <div
      className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-center justify-between gap-3"
      data-testid="v22-9-8-enrichment-banner"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-lg bg-amber-200 text-amber-700 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-amber-900">
            V22.9.8 — Asset Demography expanded
          </p>
          <p className="text-[12px] text-amber-800 truncate">
            We've added technical fields (network architecture, vendor connectivity, regulatory scope) to your Asset Profile. ~10 minutes to auto-discover — drives accuracy in BIA dependency lookups and Risk template selection.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <Button
          size="sm"
          onClick={() => navigate("/profile-update?enrich=1")}
          className="bg-amber-600 hover:bg-amber-700 text-white h-7 text-[11.5px]"
          data-testid="v22-9-8-enrichment-banner-cta"
        >
          Enrich Profile
        </Button>
        <button
          type="button"
          onClick={() => {
            localStorage.setItem(DISMISS_KEY, user.email);
            setShow(false);
          }}
          className="text-amber-700 hover:text-amber-900 p-1"
          aria-label="Dismiss"
          data-testid="v22-9-8-enrichment-banner-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
